import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { RocketLaunch as LaunchIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useSiteStore } from '@/store/site.store';
import { startSiteAudit, type SiteAuditRun } from '@/services/customer/auditApi';

export default function CustomerSiteAuditPage() {
  const { selectedSite } = useSiteStore();
  const [maxPages, setMaxPages] = useState('50');
  const [llmEnabled, setLlmEnabled] = useState(true);
  const [formError, setFormError] = useState('');
  const [started, setStarted] = useState<SiteAuditRun | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      startSiteAudit({ siteId: selectedSite!.id, maxPages: Number(maxPages), llmEnabled }),
    onSuccess: (result) => { setStarted(result); setFormError(''); },
    onError: (e: Error) => setFormError(e.message),
  });

  const handleSubmit = () => {
    if (!selectedSite) { setFormError('Select a site from the top bar first.'); return; }
    setFormError('');
    mutation.mutate();
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Start Site Audit</Typography>
        <Typography variant="body2" color="text.secondary">
          Crawl your site with the AI agent and generate SEO recommendations for every page.
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 480 }}>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {!selectedSite && (
                <Alert severity="info">Select a site from the top bar to start an audit.</Alert>
              )}

              {started && (
                <Alert
                  severity="success"
                  action={
                    <Button color="inherit" size="small" onClick={() => { setStarted(null); setMaxPages('50'); }}>
                      New
                    </Button>
                  }
                >
                  <Typography variant="body2" fontWeight={600}>
                    Site audit started — Run #{started.id}
                  </Typography>
                  <Typography variant="caption">
                    {started.totalPages > 0 ? `${started.totalPages} pages queued` : 'Pages being discovered…'}
                  </Typography>
                  <br />
                  <Typography variant="caption">
                    Track progress in{' '}
                    <Link to="/customer/audit/tracking" style={{ color: 'inherit', fontWeight: 600 }}>
                      Audit History
                    </Link>.
                  </Typography>
                </Alert>
              )}

              <TextField
                label="Max Pages"
                type="number"
                size="small"
                value={maxPages}
                onChange={(e) => setMaxPages(e.target.value)}
                inputProps={{ min: 1, max: 200 }}
                helperText="How many pages to crawl (max 200)"
                disabled={!!started}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={llmEnabled}
                    onChange={(e) => setLlmEnabled(e.target.checked)}
                    color="primary"
                    disabled={!!started}
                  />
                }
                label={
                  <Tooltip title="AI analysis generates keyword relevance, content gap, and improvement recommendations per page.">
                    <span>Enable AI Analysis</span>
                  </Tooltip>
                }
              />

              {formError && <Alert severity="error">{formError}</Alert>}

              <Button
                variant="contained"
                size="large"
                startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <LaunchIcon />}
                onClick={handleSubmit}
                disabled={!selectedSite || !!started || mutation.isPending}
              >
                {mutation.isPending ? 'Starting…' : 'Start Site Audit'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
